import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Table from "../../../components/Table";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { getInvoiceLabel } from "../../PurchaseInvoice/helpers";

/**
 * Vincular a esta compra uma nota de entrada que já existe (BDM-1052).
 *
 * A nota costuma chegar antes da conferência da compra — pela tela de notas ou
 * pelo DF-e — e até aqui o modal do pedido só sabia lançar nota nova.
 */
export const ModalLinkPurchaseInvoice = ({ show, close, purchaseOrder, onLinked, level }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [linking, setLinking] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [amount, setAmount] = useState('R$ 0,00');

    useEffect(() => {
        if (!show) return;

        setSearch('');
        setSelected(null);
    }, [show, purchaseOrder?.id]);

    // O termo muda a cada tecla; a busca espera o usuário parar de digitar.
    useEffect(() => {
        if (!show) return;

        const timer = setTimeout(() => load(search), search ? 400 : 0);

        return () => clearTimeout(timer);
    }, [show, search, purchaseOrder?.id]);

    const load = term => {
        setLoading(true);

        api.get(`/purchase-order/${purchaseOrder.id}/linkable-invoices`, { params: { search: term || undefined } })
            .then(res => setInvoices(res.data.resources ?? []))
            .catch(renderError)
            .then(() => setLoading(false));
    };

    const orderBalance = Math.max(0, (purchaseOrder?.total ?? 0) - (purchaseOrder?.invoiced_amount ?? 0));

    const select = invoice => {
        setSelected(invoice);

        const free = Math.max(0, (invoice.amount ?? 0) - (invoice.allocated_amount ?? 0));

        setAmount(convertIntToMoney(orderBalance > 0 ? Math.min(free, orderBalance) : free, true));
    };

    const link = () => {
        setLinking(true);

        api.post(`/purchase-order/${purchaseOrder.id}/invoice`, {
            purchase_invoice_id: selected.id,
            amount: convertMoenyToInt(amount) || 0,
        }).then(res => {
            toast.success(res.data.message ?? 'Nota vinculada a esta compra');
            close();
            onLinked();
        }).catch(renderError).then(() => setLinking(false));
    };

    if (!purchaseOrder) return null;

    const rows = invoices.map(invoice => {
        const free = Math.max(0, (invoice.amount ?? 0) - (invoice.allocated_amount ?? 0));

        return {
            action: () => select(invoice),
            className: selected?.id === invoice.id ? 'linkable-invoice-selected' : '',
            data: [
                getInvoiceLabel(invoice),
                invoice.company_trade_name ?? 'Emitente não identificado',
                invoice.issued_at ? moment(invoice.issued_at).format('DD/MM/YYYY') : '—',
                convertIntToMoney(invoice.amount, true),
                invoice.allocated_amount > 0
                    ? `${convertIntToMoney(free, true)} livre`
                    : 'Nenhum',
            ],
        };
    });

    return (
        <Modal
            level={level}
            className={`link-purchase-invoice-modal`}
            size={`big`}
            title={`Vincular nota ao pedido #${purchaseOrder.code}`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`link`}
                        text={`Vincular`}
                        action={link}
                        loading={linking}
                        disabled={!selected}
                    />
                </>
            }
        >
            <div className="section link-purchase-invoice">
                <Input
                    label={`Buscar por número, chave ou emitente`}
                    value={search}
                    change={setSearch}
                />

                <Table
                    header={['Nota', 'Emitente', 'Emissão', 'Valor', 'Já alocado']}
                    rows={rows}
                    loading={loading}
                />

                {!loading && invoices.length === 0 &&
                    <p className={`fiscal-hint`}>
                        Nenhuma nota deste fornecedor fora desta compra. Notas sem emitente
                        identificado também aparecem aqui — é o estado em que cai o XML de
                        um fornecedor que não está cadastrado.
                    </p>
                }

                {selected &&
                    <div className={`row`}>
                        <div className="col-4">
                            <Input
                                label={`Valor que entra nesta compra`}
                                value={amount}
                                change={setAmount}
                                mask={`convertToMoney`}
                            />
                        </div>
                        <div className="col-8">
                            <p className={`fiscal-hint`}>
                                A nota <strong>{getInvoiceLabel(selected)}</strong> vale{' '}
                                {convertIntToMoney(selected.amount, true)}
                                {selected.allocated_amount > 0 &&
                                    ` e já está alocada em ${convertIntToMoney(selected.allocated_amount, true)} em outras compras`
                                }.
                                {' '}Faltam {convertIntToMoney(orderBalance, true)} para cobrir este pedido.
                            </p>
                        </div>
                    </div>
                }
            </div>
        </Modal>
    );
};

export default ModalLinkPurchaseInvoice;
